import React from 'react';
import { Cpu, Award } from 'lucide-react';
import { technicalSkills, certifications, skills } from '../data/mock';

// Skill Category Card
const SkillCategory = ({ category, data }) => (
  <div className="bg-white rounded-2xl p-5 shadow-md border border-gray-100 hover:shadow-lg hover:border-purple-200 transition-all duration-300 group">
    <div className="flex items-center gap-3 mb-4">
      <div 
        className="w-10 h-10 rounded-xl flex items-center justify-center text-xl shadow-sm"
        style={{ backgroundColor: `${data.color}15` }}
      >
        {data.icon}
      </div>
      <h3 className="font-bold text-gray-800">{data.title}</h3>
    </div>
    <div className="flex flex-wrap gap-2">
      {data.skills.map((skill, i) => (
        <span 
          key={i}
          className="px-2.5 py-1 bg-gray-50 text-gray-700 text-xs font-medium rounded-lg border border-gray-100 group-hover:bg-purple-50 group-hover:border-purple-100 transition-colors"
        >
          {skill}
        </span>
      ))}
    </div>
  </div>
);

// Certification Badge
const CertificationBadge = ({ cert }) => (
  <div className="bg-white rounded-2xl p-4 shadow-md border border-gray-100 hover:shadow-lg hover:scale-[1.02] transition-all duration-300 cursor-pointer group">
    <div className="flex items-center gap-3">
      <div 
        className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl shadow-sm group-hover:scale-110 transition-transform"
        style={{ backgroundColor: `${cert.color}15` }}
      >
        {cert.icon}
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="font-bold text-gray-800 text-sm truncate">{cert.name}</h4>
        <p className="text-xs font-medium" style={{ color: cert.color }}>{cert.level}</p>
      </div>
      <div className="text-xs text-gray-400 font-medium">{cert.issued}</div>
    </div>
  </div>
);

// Power Level Bar
const PowerBar = ({ skill }) => (
  <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-all">
    <div className="flex items-center justify-between mb-2">
      <div className="flex items-center gap-2">
        <span className="text-lg">{skill.emoji}</span>
        <span className="font-bold text-gray-800 text-sm">{skill.name}</span>
      </div>
      <span className="text-sm font-bold" style={{ color: skill.color }}>{skill.level}%</span>
    </div>
    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
      <div
        className="h-full rounded-full transition-all duration-1000"
        style={{ 
          width: `${skill.level}%`,
          background: `linear-gradient(90deg, ${skill.color}, ${skill.color}cc)`
        }}
      />
    </div>
  </div>
);

const SkillsSection = () => {
  return (
    <section id="skills" className="py-20 bg-white relative overflow-hidden">
      {/* Background */}
      <div className="absolute top-10 right-10 text-6xl opacity-5">⚙️</div>
      <div className="absolute bottom-10 left-10 text-6xl opacity-5">🔧</div>
      
      <div className="max-w-6xl mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-100 mb-4">
            <Cpu className="w-4 h-4 text-purple-600" />
            <span className="font-bold text-purple-600 text-sm">Technical Expertise</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-black text-gray-800 mb-3">
            Technical{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">
              Skills
            </span>
          </h2>
          <p className="text-gray-500 text-base max-w-xl mx-auto">
            Comprehensive DevSecOps toolkit for modern cloud infrastructure
          </p>
        </div>

        {/* Skills Categories Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-14">
          {Object.entries(technicalSkills).map(([key, data]) => (
            <SkillCategory key={key} category={key} data={data} />
          ))}
        </div>

        {/* Power Levels */}
        <div className="mb-14">
          <h3 className="text-xl font-bold text-gray-800 mb-5 text-center">Proficiency Levels</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {skills.map((skill, i) => (
              <PowerBar key={i} skill={skill} />
            ))}
          </div>
        </div>

        {/* Certifications */}
        <div>
          <div className="flex items-center justify-center gap-2 mb-6">
            <Award className="w-5 h-5 text-amber-500" />
            <h3 className="text-xl font-bold text-gray-800">Certifications</h3>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {certifications.map((cert, i) => (
              <CertificationBadge key={i} cert={cert} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SkillsSection;
